import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralCosts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [costs, setCosts] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    title: "",
    category: "Development",
    amount: "",
    date: "",
    owner: "",
    status: "Planned",
  };

  const [formData, setFormData] = useState(initialForm);

  // -----------------------
  // SAMPLE DATA
  // -----------------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Portal" },
  ];

  const demoCosts = [
    {
      id: 1,
      projectId: 1,
      title: "UI Development",
      category: "Development",
      amount: 2000,
      date: "2026-03-10",
      owner: "Rahul",
      status: "Approved",
    },
    {
      id: 2,
      projectId: 1,
      title: "Cloud Hosting",
      category: "Infrastructure",
      amount: 500,
      date: "2026-03-12",
      owner: "Priya",
      status: "Planned",
    },
    {
      id: 3,
      projectId: 2,
      title: "ERP License",
      category: "Software",
      amount: 3000,
      date: "2026-03-15",
      owner: "Amit",
      status: "Paid",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setCosts(demoCosts);
  };

  // -----------------------
  // CRUD
  // -----------------------

  const handleAdd = () => {
    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }

    setEditingCost(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (cost) => {
    setEditingCost(cost);

    setFormData({
      title: cost.title,
      category: cost.category,
      amount: cost.amount,
      date: cost.date,
      owner: cost.owner,
      status: cost.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this cost entry?")) return;

    setCosts((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.date) {
      alert("Please enter amount and date.");
      return;
    }

    const newCost = {
      id: editingCost?.id || Date.now(),
      projectId: Number(selectedProjectId),
      ...formData,
    };

    if (editingCost) {
      setCosts((prev) =>
        prev.map((c) => (c.id === editingCost.id ? newCost : c)),
      );
    } else {
      setCosts((prev) => [...prev, newCost]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // -----------------------
  // FILTERS
  // -----------------------

  const projectCosts = costs.filter(
    (c) => String(c.projectId) === String(selectedProjectId),
  );

  const filtered = projectCosts.filter(
    (c) =>
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      (c.category || "").toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  const statusColor = {
    Planned: "bg-indigo-600",
    Approved: "bg-emerald-600",
    Paid: "bg-green-700",
    Cancelled: "bg-red-600",
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            💰 SprintCentral Project Costs
          </div>
          <div className="text-xs text-gray-400">
            Track Agile Project Expenses & Budget
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>

      <div className="mt-4 min-h-[75vh]">
        {/* ACTION BAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Costs
          </button>

          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Cost
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>

              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search costs..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>
        </div>

        {/* MODAL */}

        {showModal && (
          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingCost ? "Edit Cost" : "New Cost"}
              </h2>

              <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Cost Title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="w-48 h-12 bg-gray-900 border border-gray-700 rounded p-2"
              >
                <option>Development</option>
                <option>Infrastructure</option>
                <option>Software</option>
                <option>Operations</option>
                <option>Consulting</option>
              </select>

              <input
                type="number"
                placeholder="Amount"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                className="w-40 bg-white border border-gray-700 rounded p-2 text-black"
              />

              <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                className="w-48 bg-white border border-gray-700 rounded p-2 text-black"
              />

              <input
                type="text"
                placeholder="Owner"
                value={formData.owner}
                onChange={(e) =>
                  setFormData({ ...formData, owner: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingCost ? "Update Cost" : "Create Cost"}
              </button>
            </form>
          </div>
        )}

        {/* COST CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">
          {paginated.map((cost) => (
            <div
              key={cost.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold text-white">{cost.title}</h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${statusColor[cost.status]}`}
                >
                  {cost.status}
                </span>
              </div>

              <p className="text-xs text-cyan-300 mt-1">{cost.category}</p>

              <p className="text-lg text-green-400 mt-2">${cost.amount}</p>

              <div className="text-xs text-gray-400 mt-3">
                Date: {cost.date}
              </div>

              <div className="text-xs text-gray-400">
                Owner: {cost.owner || "-"}
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEdit(cost)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(cost.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralCosts;
